import { Capacitor, registerPlugin } from "@capacitor/core";
import { isUnimplementedNativeCall } from "./bridge-availability";

/**
 * JS side of Android's HiivePushBridgePlugin.java. Registered once here —
 * Capacitor refuses a second `registerPlugin` for the same name — and shared
 * by use-active-chat-bridge.ts (the "viewing chat" flag) and the push hooks
 * (notification channels).
 */
export type HiivePushBridgePlugin = {
  setActiveChat(input: { chatId: string }): Promise<void>;
  clearActiveChat(): Promise<void>;
  /**
   * Creates the per-type notification channels and returns their ids. Added
   * with the channel split; binaries from before it reject with
   * UNIMPLEMENTED, which is how JS learns it must stay on "hiive-default".
   */
  ensureChannels(): Promise<{ channels?: string[] }>;
};

export const HiivePushBridge =
  registerPlugin<HiivePushBridgePlugin>("HiivePushBridge");

/**
 * The single channel every binary has. Still created from JS
 * (use-push-notifications.ts) because Capacitor posts FCM `notification`
 * payloads and pre-split binaries' pushes to it.
 */
export const LEGACY_ANDROID_CHANNEL_ID = "hiive-default";

let channelsPromise: Promise<ReadonlySet<string>> | null = null;

/**
 * Asks the native layer to create its channels, once per page load, and
 * resolves to the ids that now exist. Empty on iOS, on the web, and on a
 * binary that predates the split. Channel definitions (names, importance,
 * group) live ONLY in HiivePushNotificationRenderer.java: Android freezes a
 * channel's importance at first creation, so a second definition here that
 * disagreed would win or lose depending on which side ran first.
 */
export function ensureNativePushChannels(): Promise<ReadonlySet<string>> {
  if (Capacitor.getPlatform() !== "android") {
    return Promise.resolve(new Set<string>());
  }
  if (!channelsPromise) {
    channelsPromise = HiivePushBridge.ensureChannels().then(
      (result) => new Set(result.channels ?? []),
      (error: unknown) => {
        // A real failure is retried on the next call; an old binary is an
        // answer, not a failure, and stays cached.
        if (!isUnimplementedNativeCall(error)) channelsPromise = null;
        return new Set<string>();
      },
    );
  }
  return channelsPromise;
}

/**
 * The channel a foreground LocalNotifications mirror should post to: the
 * push's own `channel` (set by src/lib/push-notifications.ts) when this
 * binary created it, otherwise the legacy channel. Posting to a channel that
 * does not exist drops the notification silently on Android 8+.
 */
export async function resolveAndroidMirrorChannel(
  data: Record<string, unknown>,
): Promise<string> {
  const requested = typeof data.channel === "string" ? data.channel : "";
  if (!requested) return LEGACY_ANDROID_CHANNEL_ID;
  const available = await ensureNativePushChannels();
  return available.has(requested) ? requested : LEGACY_ANDROID_CHANNEL_ID;
}
